import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from "react-redux";
import { useTranslation } from 'react-i18next';
import { Alert, Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { Bell, Info, Plus, Save, UserCircle, X } from "lucide-react";
import Breadcrumb from '../components/Breadcrumb';
import { ISettingState } from '../models/types';
import { getCurrentSubscription, subscribePushMessages, unsubscribePush } from '../utilities/pushMessaging';
import { IUser } from '../models/IUser';
import * as actionTypes from '../actionsTypes';
import { getUserSettings, saveUserSettings, updateCurrentUser, UserSettingsPayload } from "../actions/userSettingsActions";
import { toastError, toastSuccess } from "../actions/formInputAction";
import cookie from "../utilities/cookie";
import { Constants } from "../utilities/constants";

interface Props {
    dispatch: Function;
    setting: ISettingState;
    user: IUser;
}

type NotificationOptions = {
    receive_sensor_alerts: boolean;
    new_activity_alert: boolean;
};

type AssetTypeOption = {
    id: number;
    name: string;
    parent_id?: number | null;
};

type ToggleProps = {
    checked: boolean;
    disabled?: boolean;
    label: string;
    help?: string;
    disabledReason?: string;
    disabledAriaLabel: string;
    yesLabel: string;
    noLabel: string;
    onChange: (checked: boolean) => void;
};

const notificationDefaults: NotificationOptions = {
    receive_sensor_alerts: false,
    new_activity_alert: true,
};

const formatDisplayValue = (value: unknown): string => {
    if (!value) {
        return '-';
    }

    if (value instanceof Date) {
        return value.toLocaleString();
    }

    return String(value);
};

type Translator = (txt: string, options?: Record<string, unknown>) => string;

const inspectPushSwitchability = (pushMessaging: boolean, t: Translator): string | undefined => {
    if (!pushMessaging) {
        return t('settings_page.push_not_enabled');
    }

    if (!cookie.get(Constants.VAPID_PUBLIC_KEY)) {
        return t('settings_page.push_not_configured');
    }

    if (!('serviceWorker' in navigator)) {
        return t('settings_page.service_worker_not_supported');
    }

    if (!('PushManager' in window)) {
        return t('settings_page.push_not_supported');
    }

    if (!('Notification' in window)) {
        return t('settings_page.notification_permission_not_supported');
    }

    if (Notification.permission === 'denied') {
        return t('settings_page.notification_permission_blocked');
    }

    return undefined;
};

const YesNoToggle: React.FC<ToggleProps> = ({
    checked,
    disabled,
    label,
    help,
    disabledReason,
    disabledAriaLabel,
    yesLabel,
    noLabel,
    onChange,
}) => (
    <div className="settings-toggle-row">
        <div>
            <div className="settings-toggle-title">
                <span>{label}</span>
                {disabled && disabledReason && (
                    <button
                        type="button"
                        className="settings-info-button"
                        aria-label={disabledAriaLabel}
                        onClick={() => window.alert(disabledReason)}
                    >
                        <Info size={16} />
                    </button>
                )}
            </div>
            {help && <div className="small text-secondary">{help}</div>}
        </div>
        <button
            type="button"
            className={`settings-yes-no-toggle ${checked ? 'is-on' : 'is-off'}`}
            disabled={disabled}
            aria-pressed={checked}
            onClick={() => onChange(!checked)}
        >
            <span>{checked ? yesLabel : noLabel}</span>
            <i aria-hidden="true" />
        </button>
    </div>
);

const SettingsPage: React.FC<Props> = ({ dispatch, setting, user }) => {
    const { i18n } = useTranslation();
    // @ts-ignore
    const t: Translator = useCallback((txt: string, options?: Record<string, unknown>) => i18n.t(txt, options), [i18n]);

    const initialNotificationOptions = useMemo<NotificationOptions>(() => ({
        ...notificationDefaults,
        receive_sensor_alerts: user?.options?.notification?.receive_sensor_alerts ?? notificationDefaults.receive_sensor_alerts,
        new_activity_alert: user?.options?.notification?.new_activity_alert ?? notificationDefaults.new_activity_alert,
    }), [user?.options?.notification?.new_activity_alert, user?.options?.notification?.receive_sensor_alerts]);

    const [name, setName] = useState(user?.name ?? '');
    const [email, setEmail] = useState(user?.email ?? '');
    const [phoneNumber, setPhoneNumber] = useState(user?.phone_number ?? '');
    const [configuredTimeZone, setConfiguredTimeZone] = useState(user?.configured_time_zone ?? '');
    const [notificationOptions, setNotificationOptions] = useState<NotificationOptions>(initialNotificationOptions);
    const [timeZones, setTimeZones] = useState<Record<string, string>>({});
    const [assetTypes, setAssetTypes] = useState<AssetTypeOption[]>([]);
    const [trackedAssetTypeIds, setTrackedAssetTypeIds] = useState<number[]>(user?.options?.track_trace?.asset_type_ids ?? []);
    const [showProfileForm, setShowProfileForm] = useState(false);
    const [showAssetTypeForm, setShowAssetTypeForm] = useState(false);
    const [assetTypeToAdd, setAssetTypeToAdd] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPreference, setSavingPreference] = useState<keyof NotificationOptions | null>(null);
    const [savingAssetTypes, setSavingAssetTypes] = useState(false);
    const [pushBlockedReason, setPushBlockedReason] = useState<string | undefined>(() => inspectPushSwitchability(setting.pushMessaging, t));

    const refreshPushSwitchability = useCallback((pushMessaging: boolean) => {
        const reason = inspectPushSwitchability(pushMessaging, t);
        setPushBlockedReason(reason);

        if (reason || !('serviceWorker' in navigator)) {
            return;
        }

        navigator.serviceWorker.getRegistrations()
            .then((registrations) => {
                setPushBlockedReason(registrations.length > 0
                    ? undefined
                    : t('settings_page.service_worker_not_registered')
                );
            })
            .catch(() => {
                setPushBlockedReason(t('settings_page.service_worker_inspection_failed'));
            });
    }, [t]);

    useEffect(() => {
        setName(user?.name ?? '');
        setEmail(user?.email ?? '');
        setPhoneNumber(user?.phone_number ?? '');
        setConfiguredTimeZone(user?.configured_time_zone ?? '');
        setNotificationOptions(initialNotificationOptions);
        setTrackedAssetTypeIds(user?.options?.track_trace?.asset_type_ids ?? []);
    }, [initialNotificationOptions, user]);

    useEffect(() => {
        getUserSettings()
            .then((settings) => {
                setTimeZones(settings.time_zones || {});
                setAssetTypes(settings.asset_types || []);
                dispatch({
                    type: actionTypes.USE_PUSH_MESSAGES,
                    payload: { uses_push: Boolean(settings.push_messaging_enabled) },
                });

                if (settings.public_vapid) {
                    cookie.put(Constants.VAPID_PUBLIC_KEY, settings.public_vapid);
                } else {
                    cookie.remove(Constants.VAPID_PUBLIC_KEY);
                }

                refreshPushSwitchability(Boolean(settings.push_messaging_enabled));
                if (settings.user) {
                    dispatch(updateCurrentUser(settings.user));
                }
            })
            .catch((error) => {
                dispatch(toastError(error, t('settings_page.settings_error'), t('settings_page.settings_load_failed')));
            });
    }, [dispatch, refreshPushSwitchability, t]);

    useEffect(() => {
        refreshPushSwitchability(setting.pushMessaging);
    }, [refreshPushSwitchability, setting.pushMessaging]);

    useEffect(() => {
        if (!setting.pushMessaging) {
            return;
        }

        getCurrentSubscription()
            .then((subscription) => {
                dispatch({
                    type: actionTypes.PUSH_SUBSCRIBED,
                    payload: { push_subscribed: subscription ? 1 : 0 },
                });
            })
            .catch(() => {
                dispatch({
                    type: actionTypes.PUSH_SUBSCRIBED,
                    payload: { push_subscribed: 0 },
                });
            });
    }, [dispatch, setting.pushMessaging]);

    const buildPayload = (nextOptions: NotificationOptions, nextTrackedAssetTypeIds: number[]): UserSettingsPayload => ({
        name,
        email,
        phone_number: phoneNumber || undefined,
        configured_time_zone: configuredTimeZone || undefined,
        options: {
            ...(user?.options ?? {}),
            notification: {
                ...(user?.options?.notification ?? {}),
                ...nextOptions,
            },
            track_trace: {
                ...(user?.options?.track_trace ?? {}),
                asset_type_ids: nextTrackedAssetTypeIds,
            },
        },
    });

    const persistUserSettings = async (nextOptions: NotificationOptions, nextTrackedAssetTypeIds = trackedAssetTypeIds) => {
        const updatedUser = await saveUserSettings(buildPayload(nextOptions, nextTrackedAssetTypeIds));
        dispatch(updateCurrentUser(updatedUser));
        return updatedUser;
    };

    const ensurePushSubscription = async () => {
        if (!setting.pushMessaging) {
            throw new Error(t('settings_page.push_not_enabled'));
        }

        const subscribed = await subscribePushMessages(user);
        if (!subscribed) {
            throw new Error(t('settings_page.notification_permission_not_granted'));
        }

        dispatch({
            type: actionTypes.PUSH_SUBSCRIBED,
            payload: { push_subscribed: 1 },
        });
    };

    const handleProfileSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setSavingProfile(true);

        try {
            await persistUserSettings(notificationOptions, trackedAssetTypeIds);
            setShowProfileForm(false);
            dispatch(toastSuccess(t('settings_page.settings_saved'), t('settings_page.information_updated')));
        } catch (error) {
            dispatch(toastError(error, t('settings_page.settings_error'), t('settings_page.information_update_failed')));
        } finally {
            setSavingProfile(false);
        }
    };

    const handleNotificationChange = async (key: keyof NotificationOptions, checked: boolean) => {
        const blockedReason = inspectPushSwitchability(setting.pushMessaging, t);
        if (blockedReason) {
            setPushBlockedReason(blockedReason);
            window.alert(blockedReason);
            return;
        }

        const previousOptions = notificationOptions;
        const nextOptions = {
            ...notificationOptions,
            [key]: checked,
        };

        setNotificationOptions(nextOptions);
        setSavingPreference(key);

        try {
            if (checked) {
                await ensurePushSubscription();
            }

            await persistUserSettings(nextOptions, trackedAssetTypeIds);

            if (!nextOptions.receive_sensor_alerts && !nextOptions.new_activity_alert) {
                await unsubscribePush(user);
                dispatch({
                    type: actionTypes.PUSH_SUBSCRIBED,
                    payload: { push_subscribed: -1 },
                });
            }

            dispatch(toastSuccess(t('settings_page.preference_saved'), t('settings_page.preference_updated')));
        } catch (error) {
            setNotificationOptions(previousOptions);
            dispatch(toastError(error, t('settings_page.preference_error'), t('settings_page.preference_update_failed')));
        } finally {
            setSavingPreference(null);
        }
    };

    const persistTrackedAssetTypes = async (nextIds: number[]) => {
        const previousIds = trackedAssetTypeIds;
        setTrackedAssetTypeIds(nextIds);
        setSavingAssetTypes(true);

        try {
            await persistUserSettings(notificationOptions, nextIds);
            dispatch(toastSuccess(t('settings_page.product_groups_saved'), t('settings_page.product_groups_updated')));
        } catch (error) {
            setTrackedAssetTypeIds(previousIds);
            dispatch(toastError(error, t('settings_page.product_group_error'), t('settings_page.product_groups_update_failed')));
        } finally {
            setSavingAssetTypes(false);
        }
    };

    const removeTrackedAssetType = (assetTypeId: number) => {
        persistTrackedAssetTypes(trackedAssetTypeIds.filter((id) => id !== assetTypeId));
    };

    const addTrackedAssetType = () => {
        const id = Number(assetTypeToAdd);
        if (!id || trackedAssetTypeIds.includes(id)) {
            return;
        }

        persistTrackedAssetTypes([...trackedAssetTypeIds, id]);
        setAssetTypeToAdd('');
        setShowAssetTypeForm(false);
    };

    const pushDisabled = !!pushBlockedReason || !!savingPreference;
    const trackedAssetTypes = assetTypes.filter((assetType) => trackedAssetTypeIds.includes(assetType.id));
    const availableAssetTypes = assetTypes.filter((assetType) => !trackedAssetTypeIds.includes(assetType.id));
    const timeZoneOptions = Object.entries(timeZones);

    return (
        <Container className="settings-page py-3">
            <Breadcrumb pageName={t("settings_page.screen_name")} />

            <section className="settings-panel">
                <div className="settings-section-header">
                    <UserCircle size={22} />
                    <h2>{t('settings_page.information')}</h2>
                </div>

                <dl className="settings-user-summary">
                    <div>
                        <dt>{t('settings_page.login')}</dt>
                        <dd>{formatDisplayValue(user?.login)}</dd>
                    </div>
                    <div>
                        <dt>{t('settings_page.last_seen')}</dt>
                        <dd>{formatDisplayValue(user?.last_seen)}</dd>
                    </div>
                    <div>
                        <dt>{t('settings_page.role')}</dt>
                        <dd>{user?.is_admin ? t('settings_page.administrator') : user?.is_workforce ? t('settings_page.workforce') : t('settings_page.user')}</dd>
                    </div>
                </dl>

                <button type="button" className="settings-text-link" onClick={() => setShowProfileForm((visible) => !visible)}>
                    {showProfileForm ? t('settings_page.hide_profile_form') : t('settings_page.edit_profile_information')}
                </button>

                {showProfileForm && (
                    <Form onSubmit={handleProfileSubmit} className="settings-profile-form">
                        <Row className="g-3">
                            <Col xs={12}>
                                <Form.Group controlId="settings-name">
                                    <Form.Label>{t('settings_page.name')}</Form.Label>
                                    <Form.Control value={name} onChange={(event) => setName(event.target.value)} required />
                                </Form.Group>
                            </Col>
                            <Col xs={12}>
                                <Form.Group controlId="settings-email">
                                    <Form.Label>{t('settings_page.email')}</Form.Label>
                                    <Form.Control type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
                                </Form.Group>
                            </Col>
                            <Col xs={12}>
                                <Form.Group controlId="settings-phone">
                                    <Form.Label>{t('settings_page.phone_number')}</Form.Label>
                                    <Form.Control value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col xs={12}>
                                <Form.Group controlId="settings-time-zone">
                                    <Form.Label>{t('settings_page.time_zone')}</Form.Label>
                                    <Form.Select value={configuredTimeZone} onChange={(event) => setConfiguredTimeZone(event.target.value)}>
                                        <option value="">{user?.login_time_zone || 'Europe/Amsterdam'}</option>
                                        {timeZoneOptions.map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col xs={12}>
                                <Button type="submit" variant="primary" className="settings-save-button" disabled={savingProfile}>
                                    {savingProfile ? <Spinner size="sm" /> : <Save size={16} />}
                                    <span>{t('settings_page.save_information')}</span>
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                )}
            </section>

            <section className="settings-panel">
                <div className="settings-section-header">
                    <Bell size={22} />
                    <h2>{t('settings_page.personalization')}</h2>
                </div>

                {pushBlockedReason && (
                    <Alert variant="warning" className="mb-3">
                        {pushBlockedReason}
                    </Alert>
                )}

                <YesNoToggle
                    label={t('settings_page.receive_sensor_alerts')}
                    help={t('settings_page.receive_sensor_alerts_help')}
                    checked={notificationOptions.receive_sensor_alerts}
                    disabled={pushDisabled}
                    disabledReason={pushBlockedReason}
                    disabledAriaLabel={t('settings_page.why_cannot_change', { label: t('settings_page.receive_sensor_alerts') })}
                    yesLabel={t('settings_page.yes')}
                    noLabel={t('settings_page.no')}
                    onChange={(checked) => handleNotificationChange('receive_sensor_alerts', checked)}
                />
                <YesNoToggle
                    label={t('settings_page.new_activity_alert')}
                    help={t('settings_page.new_activity_alert_help')}
                    checked={notificationOptions.new_activity_alert}
                    disabled={pushDisabled}
                    disabledReason={pushBlockedReason}
                    disabledAriaLabel={t('settings_page.why_cannot_change', { label: t('settings_page.new_activity_alert') })}
                    yesLabel={t('settings_page.yes')}
                    noLabel={t('settings_page.no')}
                    onChange={(checked) => handleNotificationChange('new_activity_alert', checked)}
                />

                <div className="settings-product-groups">
                    <div>
                        <div className="fw-semibold">{t('settings_page.tracked_product_group')}</div>
                        <div className="small text-secondary">{t('settings_page.tracked_product_group_help')}</div>
                    </div>

                    <div className="settings-product-group-list">
                        {trackedAssetTypes.length === 0 && (
                            <div className="settings-product-group-empty">{t('settings_page.all_product_groups_available')}</div>
                        )}

                        {trackedAssetTypes.map((assetType) => (
                            <div className="settings-product-group-item" key={assetType.id}>
                                <span>{assetType.name}</span>
                                <Button
                                    variant="link"
                                    className="settings-product-group-remove"
                                    disabled={savingAssetTypes}
                                    onClick={() => removeTrackedAssetType(assetType.id)}
                                    aria-label={t('settings_page.remove_asset_type', { name: assetType.name })}
                                >
                                    <X size={17} />
                                </Button>
                            </div>
                        ))}

                        {!showAssetTypeForm && (
                            <Button
                                variant="outline-primary"
                                className="settings-add-product-group"
                                disabled={savingAssetTypes || availableAssetTypes.length === 0}
                                onClick={() => setShowAssetTypeForm(true)}
                            >
                                <Plus size={16} />
                                <span>{t('settings_page.add_asset_type')}</span>
                            </Button>
                        )}

                        {showAssetTypeForm && (
                            <div className="settings-product-group-add-form">
                                <Form.Select value={assetTypeToAdd} onChange={(event) => setAssetTypeToAdd(event.target.value)} aria-label={t('settings_page.asset_type')}>
                                    <option value="">{t('settings_page.select_asset_type')}</option>
                                    {availableAssetTypes.map((assetType) => (
                                        <option key={assetType.id} value={assetType.id}>{assetType.name}</option>
                                    ))}
                                </Form.Select>
                                <Button variant="primary" disabled={savingAssetTypes || !assetTypeToAdd} onClick={addTrackedAssetType}>
                                    {t('settings_page.add')}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </Container>
    );
}

const mapStateToProps = (state: any) => ({
    setting: state.setting,
    user: state.auth.currentUser,
});

export default connect(mapStateToProps)(SettingsPage);
